// Qué hace falta para publicar un torneo (etapa 7: se cobra por torneo publicado).
//
// Armar el torneo y verlo en borrador es gratis. Publicarlo necesita una
// HABILITACIÓN, que es de ese torneo, lleva un tope de parejas (o ninguno) y no
// vence: un torneo publicado queda visible para siempre.
//
// Tres caminos (decididos por Marcos el 2026-10-01):
//   prueba  un torneo chico de un club al que el dueño le ofreció una prueba
//   pago    lo habilita el dueño al cobrar, con el tope que corresponda
//   regalo  lo habilita el dueño sin cobrar
//
// Puro: la API decide con esto, y la pantalla lo usa para explicar.

import { bien, mal, type Resultado } from './validar';

export const TIPOS_DE_HABILITACION = ['prueba', 'pago', 'regalo'] as const;
export type TipoDeHabilitacion = (typeof TIPOS_DE_HABILITACION)[number];

export interface Habilitacion {
  tipo: TipoDeHabilitacion;
  /** null = sin tope. */
  tope_parejas: number | null;
}

/**
 * Hasta cuántas parejas puede tener el torneo de prueba si el dueño todavía no
 * lo configuró. 0 = no hay prueba gratis. Marcos lo va a definir; se cambia
 * desde su panel, sin publicar una versión.
 */
export const TOPE_DE_PRUEBA_POR_DEFECTO = 8;
export const TOPE_MAXIMO = 500;
export const LARGO_MAXIMO_TEXTO_DE_PAGO = 400;

export const TEXTO_DE_PAGO_POR_DEFECTO = 'Para publicar este torneo, escribile a quien te dio el acceso.';

export type Veredicto =
  | { ok: true; /** Hay que crear la habilitación de prueba, con este tope. */ crearPrueba: number | null }
  | { ok: false; motivo: 'sin_habilitar' | 'tope'; /** El tope que se pasó (motivo 'tope'). */ tope: number | null };

/**
 * ¿Se puede publicar? `parejas` = inscriptas en todo el torneo.
 * `pruebaDisponible` = el dueño la habilitó para ese club y todavía no la usó.
 */
export function puedePublicar(
  parejas: number,
  habilitacion: Habilitacion | null,
  pruebaDisponible: boolean,
  topeDePrueba: number,
): Veredicto {
  if (habilitacion) {
    if (habilitacion.tope_parejas !== null && parejas > habilitacion.tope_parejas) {
      return { ok: false, motivo: 'tope', tope: habilitacion.tope_parejas };
    }
    return { ok: true, crearPrueba: null };
  }
  if (pruebaDisponible && topeDePrueba > 0 && parejas <= topeDePrueba) return { ok: true, crearPrueba: topeDePrueba };
  return { ok: false, motivo: 'sin_habilitar', tope: pruebaDisponible && topeDePrueba > 0 ? topeDePrueba : null };
}

/**
 * ¿Se pueden sumar `nuevas` parejas? En borrador, siempre. Con el torneo ya
 * publicado, hasta el tope: si no, alcanzaría con publicar chico y agrandar.
 */
export function puedeSumarParejas(
  publicado: boolean,
  habilitacion: Habilitacion | null,
  parejas: number,
  nuevas: number,
): { ok: true } | { ok: false; tope: number } {
  if (!publicado || !habilitacion || habilitacion.tope_parejas === null) return { ok: true };
  return parejas + nuevas <= habilitacion.tope_parejas ? { ok: true } : { ok: false, tope: habilitacion.tope_parejas };
}

/** "hasta 16 parejas" / "sin tope de parejas" */
export function textoDeTope(tope: number | null): string {
  return tope === null ? 'sin tope de parejas' : `hasta ${tope} parejas`;
}

const NOMBRE_TIPO: Record<TipoDeHabilitacion, string> = { prueba: 'prueba gratis', pago: 'pago', regalo: 'regalo' };
export const nombreDeTipo = (t: TipoDeHabilitacion) => NOMBRE_TIPO[t];

function objeto(cuerpo: unknown): Record<string, unknown> {
  return typeof cuerpo === 'object' && cuerpo !== null ? (cuerpo as Record<string, unknown>) : {};
}

function topeValido(v: unknown, minimo: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= minimo && v <= TOPE_MAXIMO;
}

/** Lo que manda el dueño al habilitar: pago o regalo (la prueba la crea la app). */
export function validarHabilitacion(cuerpo: unknown): Resultado<Habilitacion & { tipo: 'pago' | 'regalo'; nota: string | null }> {
  const c = objeto(cuerpo);
  if (c.tipo !== 'pago' && c.tipo !== 'regalo') return mal('Decí si es un pago o un regalo.');
  const tope = c.tope_parejas ?? null;
  if (tope !== null && !topeValido(tope, 2)) return mal(`El tope de parejas va de 2 a ${TOPE_MAXIMO}, o vacío para que no tenga.`);
  const nota = typeof c.nota === 'string' ? c.nota.replace(/\s+/g, ' ').trim().slice(0, 120) : '';
  return bien({ tipo: c.tipo, tope_parejas: tope, nota: nota || null });
}

/**
 * Cada cuántos minutos se actualiza sola la página pública de un torneo.
 *
 * Decidido por Marcos el 2026-10-01: 5 minutos y un botón "Actualizar", en vez
 * de los 30 segundos de antes. Con 150 personas mirando 12 horas, 30 s son
 * 216.000 pedidos por día (el plan gratuito de Workers da 100.000) y 5 minutos
 * son 21.600. El que quiere el dato ya, toca el botón.
 *
 * Y es parte de lo que se paga: el torneo de PRUEBA gratis se actualiza más
 * espaciado que uno habilitado (pago o regalo). Los dos números los cambia el
 * dueño desde su panel: con el plan pago de Cloudflare puede bajar el primero.
 */
export const MINUTOS_PAGO_POR_DEFECTO = 5;
export const MINUTOS_PRUEBA_POR_DEFECTO = 15;
export const MINUTOS_MINIMO = 1;
export const MINUTOS_MAXIMO = 120;

export interface Configuracion {
  /** 0 = sin prueba gratis. */
  prueba_tope_parejas: number;
  texto_de_pago: string;
  /** Cada cuántos minutos se actualiza sola la página de un torneo pago o de regalo. */
  minutos_pago: number;
  /** Lo mismo para el torneo de prueba gratis. */
  minutos_prueba: number;
}

/** Cada cuántos segundos tiene que volver a pedir la página pública de ese torneo. */
export function segundosDeActualizacion(tipo: TipoDeHabilitacion | null, c: Pick<Configuracion, 'minutos_pago' | 'minutos_prueba'>): number {
  return (tipo === 'prueba' ? c.minutos_prueba : c.minutos_pago) * 60;
}

export function validarConfiguracion(cuerpo: unknown): Resultado<Configuracion> {
  const c = objeto(cuerpo);
  if (!topeValido(c.prueba_tope_parejas, 0)) return mal(`El tope de la prueba va de 0 (sin prueba gratis) a ${TOPE_MAXIMO}.`);
  if (c.prueba_tope_parejas === 1) return mal('Con 1 pareja no hay torneo: poné 0 para sacar la prueba, o 2 en adelante.');
  if (typeof c.texto_de_pago !== 'string') return mal('Falta el texto de cómo pagar.');
  const texto = c.texto_de_pago.trim();
  if (texto.length < 5) return mal('Escribí cómo se paga (alias, link, a quién avisarle).');
  if (texto.length > LARGO_MAXIMO_TEXTO_DE_PAGO) return mal(`El texto es muy largo (máx. ${LARGO_MAXIMO_TEXTO_DE_PAGO}).`);

  const minutos = (v: unknown, porDefecto: number): number | null => {
    if (v === undefined || v === null) return porDefecto;
    return typeof v === 'number' && Number.isInteger(v) && v >= MINUTOS_MINIMO && v <= MINUTOS_MAXIMO ? v : null;
  };
  const minutos_pago = minutos(c.minutos_pago, MINUTOS_PAGO_POR_DEFECTO);
  const minutos_prueba = minutos(c.minutos_prueba, MINUTOS_PRUEBA_POR_DEFECTO);
  if (minutos_pago === null || minutos_prueba === null) {
    return mal(`Los minutos de actualización van de ${MINUTOS_MINIMO} a ${MINUTOS_MAXIMO}.`);
  }
  return bien({ prueba_tope_parejas: c.prueba_tope_parejas, texto_de_pago: texto, minutos_pago, minutos_prueba });
}
